import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle, ChevronDown, ChevronRight, Copy, Plus, Trash2,
} from 'lucide-react';
import toast from 'react-hot-toast';

import { Modal } from '@/components/ui/Modal';
import { NumberInput } from '@/components/ui/NumberInput';
import { Spinner } from '@/components/ui/Spinner';
import {
  copyDishTechCard, createStock, getDishTechCard, getModifierTechCard, getPrepRecipe, listStock,
  listDishes, saveModifierTechCard, savePrepRecipe, saveDishTechCard, setStockItemKind,
  type StockItem,
} from '@/services/admin';
import { formatRub } from '@/utils/format';
import { CANONICAL_UNITS, formatQuantity, trimNumber } from '@/utils/units';
import { IngredientPicker } from './IngredientPicker';
import {
  MAX_LOSS_PERCENT, PrepCostPanel, YieldCoefficient, YieldInputs, YieldModeNote, YieldModeSwitch, YieldResult,
  bulkFactor, fmtPercent, fmtYield, rawUnitOf, round6, sumRaw, type YieldMode,
} from './PrepYield';
import { QuantityField, quantityHint } from './QuantityField';

/** Строка редактора: ключ нужен, потому что позиция может быть ещё не выбрана. */
type Row = { key: string; itemId: string; quantity: number };

/** Где мы находимся: тех-карта блюда или рецепт заготовки внутри неё. */
type Frame =
  | { kind: 'dish'; id: string; name: string }
  | { kind: 'prep'; id: string; name: string };

const newKey = () => `row-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;

/**
 * Тех-карта блюда с заготовками внутри.
 *
 * Ключевое отличие от прежнего редактора: ингредиентом может быть заготовка
 * (соус, тесто), и её состав правится здесь же — модалка работает как стек с
 * хлебными крошками «Пицца 4 сыра → Тесто», а не отправляет владельца в другой
 * раздел с потерей набранного.
 */
export function TechCardEditor({
  dishId,
  dishName,
  dishPrice,
  onClose,
  onSaved,
}: {
  dishId: string;
  dishName: string;
  dishPrice: number;
  onClose: () => void;
  onSaved?: () => void;
}) {
  const qc = useQueryClient();
  const [stack, setStack] = useState<Frame[]>([{ kind: 'dish', id: dishId, name: dishName }]);
  const frame = stack[stack.length - 1];

  const { data: items = [], isLoading: itemsLoading } = useQuery({
    queryKey: ['admin/stock/items-all'],
    queryFn: () => listStock(),
  });

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ['admin/stock/items-all'] });
    qc.invalidateQueries({ queryKey: ['admin/stock/items'] });
    qc.invalidateQueries({ queryKey: ['admin/stock/tech-cards'] });
    qc.invalidateQueries({ queryKey: ['admin/stock/preps'] });
    qc.invalidateQueries({ queryKey: ['admin/menu/dishes'] });
    qc.invalidateQueries({ queryKey: ['admin/dishes'] });
  };

  const title = stack.length === 1
    ? `Тех-карта: ${dishName}`
    : stack.map((f) => f.name).join(' → ');

  return (
    <Modal open onClose={onClose} title={title} size="lg" footer={null}>
      {stack.length > 1 && (
        <button
          type="button"
          className="text-sm text-indigo-600 hover:underline mb-3"
          onClick={() => setStack((s) => s.slice(0, -1))}
        >
          ← Назад к «{stack[stack.length - 2].name}»
        </button>
      )}

      {itemsLoading ? <Spinner /> : frame.kind === 'dish' ? (
        <DishPane
          dishId={frame.id}
          dishPrice={dishPrice}
          items={items}
          onOpenPrep={(id, name) => setStack((s) => [...s, { kind: 'prep', id, name }])}
          onChanged={invalidateAll}
          onClose={onClose}
          onSaved={onSaved}
        />
      ) : (
        <PrepPane
          key={frame.id}
          prepId={frame.id}
          items={items}
          onOpenPrep={(id, name) => setStack((s) => [...s, { kind: 'prep', id, name }])}
          onChanged={invalidateAll}
          onDone={() => setStack((s) => s.slice(0, -1))}
        />
      )}
    </Modal>
  );
}

/**
 * Состав заготовки отдельной модалкой — для вкладки «Заготовки».
 * Тот же стек: внутри заготовки может быть другая заготовка.
 */
export function PrepRecipeModal({
  prepId,
  prepName,
  onClose,
  onSaved,
}: {
  prepId: string;
  prepName: string;
  onClose: () => void;
  onSaved?: () => void;
}) {
  const qc = useQueryClient();
  const [stack, setStack] = useState<Frame[]>([{ kind: 'prep', id: prepId, name: prepName }]);
  const frame = stack[stack.length - 1];

  const { data: items = [], isLoading } = useQuery({
    queryKey: ['admin/stock/items-all'],
    queryFn: () => listStock(),
  });

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ['admin/stock/items-all'] });
    qc.invalidateQueries({ queryKey: ['admin/stock/items'] });
    qc.invalidateQueries({ queryKey: ['admin/stock/preps'] });
    qc.invalidateQueries({ queryKey: ['admin/stock/tech-cards'] });
    onSaved?.();
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={stack.map((f) => f.name).join(' → ')}
      size="lg"
      footer={null}
    >
      {stack.length > 1 && (
        <button
          type="button"
          className="text-sm text-indigo-600 hover:underline mb-3"
          onClick={() => setStack((s) => s.slice(0, -1))}
        >
          ← Назад к «{stack[stack.length - 2].name}»
        </button>
      )}
      {isLoading ? <Spinner /> : (
        <PrepPane
          key={frame.id}
          prepId={frame.id}
          items={items}
          onOpenPrep={(id, name) => setStack((s) => [...s, { kind: 'prep', id, name }])}
          onChanged={invalidateAll}
          onDone={() => {
            if (stack.length > 1) setStack((s) => s.slice(0, -1));
            else onClose();
          }}
        />
      )}
    </Modal>
  );
}

/**
 * Тех-карта модификатора: что уходит со склада, когда гость выбирает его в
 * блюде («сироп +20 мл»). Списывается при продаже вместе с блюдом.
 */
export function ModifierTechCardModal({
  modifierId,
  modifierName,
  modifierPrice,
  onClose,
  onSaved,
}: {
  modifierId: string;
  modifierName: string;
  modifierPrice: number;
  onClose: () => void;
  onSaved?: () => void;
}) {
  const qc = useQueryClient();
  const [stack, setStack] = useState<Array<{ id: string; name: string }>>([]);
  const prep = stack[stack.length - 1];

  const { data: items = [], isLoading } = useQuery({
    queryKey: ['admin/stock/items-all'],
    queryFn: () => listStock(),
  });

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ['admin/stock/items-all'] });
    qc.invalidateQueries({ queryKey: ['admin/stock/items'] });
    qc.invalidateQueries({ queryKey: ['admin/stock/preps'] });
    qc.invalidateQueries({ queryKey: ['admin/mods'] });
    qc.invalidateQueries({ queryKey: ['admin/stock/modifier-tech-card'] });
  };

  const title = prep
    ? [modifierName, ...stack.map((f) => f.name)].join(' → ')
    : `Тех-карта: ${modifierName}`;

  return (
    <Modal open onClose={onClose} title={title} size="lg" footer={null}>
      {prep && (
        <button
          type="button"
          className="text-sm text-indigo-600 hover:underline mb-3"
          onClick={() => setStack((s) => s.slice(0, -1))}
        >
          ← Назад к «{stack.length > 1 ? stack[stack.length - 2].name : modifierName}»
        </button>
      )}
      {isLoading ? <Spinner /> : prep ? (
        <PrepPane
          key={prep.id}
          prepId={prep.id}
          items={items}
          onOpenPrep={(id, name) => setStack((s) => [...s, { id, name }])}
          onChanged={invalidateAll}
          onDone={() => setStack((s) => s.slice(0, -1))}
        />
      ) : (
        <ModifierPane
          modifierId={modifierId}
          modifierPrice={modifierPrice}
          items={items}
          onOpenPrep={(id, name) => setStack((s) => [...s, { id, name }])}
          onChanged={invalidateAll}
          onClose={onClose}
          onSaved={onSaved}
        />
      )}
    </Modal>
  );
}

function ModifierPane({
  modifierId, modifierPrice, items, onOpenPrep, onChanged, onClose, onSaved,
}: {
  modifierId: string;
  modifierPrice: number;
  items: StockItem[];
  onOpenPrep: (id: string, name: string) => void;
  onChanged: () => void;
  onClose: () => void;
  onSaved?: () => void;
}) {
  const [rows, setRows] = useState<Row[] | null>(null);
  const [creatingFor, setCreatingFor] = useState<{ key: string; name: string } | null>(null);
  const focusRow = useRef<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin/stock/modifier-tech-card', modifierId],
    queryFn: () => getModifierTechCard(modifierId),
  });

  useEffect(() => {
    if (!data || rows !== null) return;
    setRows(data.items.map((r) => ({ key: r.id, itemId: r.stockItemId, quantity: r.quantity })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const draft = rows ?? [];
  const usedIds = new Set(draft.map((r) => r.itemId).filter(Boolean));
  const itemById = (id: string) => items.find((i) => i.id === id);
  const lineKop = (r: Row) => Math.round(r.quantity * (itemById(r.itemId)?.costPerUnit ?? 0));
  const totalKop = draft.reduce((s, r) => s + lineKop(r), 0);

  const addRow = () => {
    const key = newKey();
    focusRow.current = key;
    setRows([...draft, { key, itemId: '', quantity: 0 }]);
  };
  const patchRow = (key: string, patch: Partial<Row>) =>
    setRows(draft.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  const errors: string[] = [];
  if (draft.some((r) => !r.itemId)) errors.push('Выберите ингредиент во всех строках');
  if (draft.some((r) => !(r.quantity > 0))) errors.push('Расход должен быть больше 0');
  if (new Set(draft.map((r) => r.itemId).filter(Boolean)).size !== draft.filter((r) => r.itemId).length) {
    errors.push('Один ингредиент указан несколько раз');
  }

  const saveMut = useMutation({
    mutationFn: (payload: Array<{ stockItemId: string; quantity: number }>) =>
      saveModifierTechCard(modifierId, payload),
    onSuccess: (_res, payload) => {
      toast.success(payload.length ? 'Тех-карта модификатора сохранена' : 'Тех-карта модификатора очищена');
      onChanged();
      onSaved?.();
      onClose();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Не удалось сохранить'),
  });

  if (isLoading || rows === null) return <Spinner />;

  return (
    <div className="grid gap-3">
      <p className="text-sm text-slate-500">
        Расход на один выбор модификатора в одной порции. При продаже спишется вместе с тех-картой блюда.
      </p>

      {draft.length === 0 ? (
        <div className="text-center text-slate-400 py-6 text-sm border border-dashed border-slate-200 rounded-md">
          Пока нет ни одного ингредиента
        </div>
      ) : (
        <div className="grid gap-2">
          <div className="hidden sm:grid grid-cols-[1fr_170px_100px_36px] gap-2 text-[11px] uppercase tracking-wide text-slate-400 px-1">
            <div>Ингредиент</div>
            <div className="text-right">Расход</div>
            <div className="text-right">Стоимость</div>
            <div />
          </div>
          {draft.map((row) => (
            <IngredientRow
              key={row.key}
              row={row}
              items={items}
              usedIds={usedIds}
              autoFocus={focusRow.current === row.key}
              onSelect={(item) => patchRow(row.key, { itemId: item.id })}
              onCreate={(name) => setCreatingFor({ key: row.key, name })}
              onQuantity={(q) => patchRow(row.key, { quantity: q })}
              onRemove={() => setRows(draft.filter((r) => r.key !== row.key))}
              onEnter={addRow}
              onOpenPrep={onOpenPrep}
              onMakePrep={onChanged}
              lineKop={lineKop(row)}
            />
          ))}
        </div>
      )}

      <button type="button" className="btn-outline self-start" onClick={addRow}>
        <Plus size={14} /> Добавить ингредиент
      </button>

      <CostSummary
        totalKop={totalKop}
        perUnitKop={totalKop}
        perUnitLabel="Себестоимость одного выбора"
        priceKop={modifierPrice > 0 ? modifierPrice : undefined}
        priceLabel="Цена модификатора"
        marginKop={modifierPrice - totalKop}
        foodCostPct={modifierPrice > 0 ? Math.round((totalKop / modifierPrice) * 100) : null}
      />

      <Warnings rows={draft} items={items} foodCostPct={null} portions={1} />

      {errors.length > 0 && (
        <ul className="text-sm text-rose-600 list-disc pl-5">
          {errors.map((e) => <li key={e}>{e}</li>)}
        </ul>
      )}

      <div className="flex flex-wrap justify-end gap-2 border-t border-slate-100 pt-3">
        {data && data.items.length > 0 && (
          <button
            className="btn-outline text-rose-600 border-rose-200 hover:bg-rose-50 mr-auto"
            onClick={() => { if (confirm('Очистить тех-карту модификатора?')) saveMut.mutate([]); }}
            disabled={saveMut.isPending}
          >
            <Trash2 size={14} /> Очистить
          </button>
        )}
        <button className="btn-outline" onClick={onClose} disabled={saveMut.isPending}>Отмена</button>
        <button
          className="btn-primary"
          onClick={() => saveMut.mutate(draft.map((r) => ({ stockItemId: r.itemId, quantity: r.quantity })))}
          disabled={saveMut.isPending || errors.length > 0}
        >
          {saveMut.isPending ? 'Сохраняем…' : 'Сохранить'}
        </button>
      </div>

      {creatingFor && (
        <CreateStockItemModal
          initialName={creatingFor.name}
          onClose={() => setCreatingFor(null)}
          onCreated={(item) => {
            patchRow(creatingFor.key, { itemId: item.id });
            setCreatingFor(null);
            onChanged();
          }}
        />
      )}
    </div>
  );
}

// ─── Тех-карта блюда ─────────────────────────────────────────────────────

function DishPane({
  dishId, dishPrice, items, onOpenPrep, onChanged, onClose, onSaved,
}: {
  dishId: string;
  dishPrice: number;
  items: StockItem[];
  onOpenPrep: (id: string, name: string) => void;
  onChanged: () => void;
  onClose: () => void;
  onSaved?: () => void;
}) {
  const [rows, setRows] = useState<Row[] | null>(null);
  const [portions, setPortions] = useState(1);
  const [manualCost, setManualCost] = useState<number | null>(null);
  const [showCopy, setShowCopy] = useState(false);
  const [creatingFor, setCreatingFor] = useState<{ key: string; name: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const focusRow = useRef<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin/stock/dish-tech-card', dishId],
    queryFn: () => getDishTechCard(dishId),
  });

  // Раскладку показываем в том масштабе, в котором её вводили: в БД лежит расход
  // на одну порцию, а человек считал «на кастрюлю из 10».
  useEffect(() => {
    if (!data || rows !== null) return;
    const p = data.portions && data.portions > 0 ? data.portions : 1;
    setPortions(p);
    setManualCost(data.manualCostPrice ?? null);
    setRows(data.items.map((r) => ({ key: r.id, itemId: r.stockItemId, quantity: r.quantity * p })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const draft = rows ?? [];
  const usedIds = new Set(draft.map((r) => r.itemId).filter(Boolean));

  const addRow = () => {
    const key = newKey();
    focusRow.current = key;
    setRows([...draft, { key, itemId: '', quantity: 0 }]);
  };
  const patchRow = (key: string, patch: Partial<Row>) =>
    setRows(draft.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const removeRow = (key: string) => setRows(draft.filter((r) => r.key !== key));

  const itemById = (id: string) => items.find((i) => i.id === id);
  // Стоимость строки — на весь введённый масштаб; себестоимость порции делим.
  const lineKop = (r: Row) => Math.round(r.quantity * (itemById(r.itemId)?.costPerUnit ?? 0));
  const totalKop = draft.reduce((s, r) => s + lineKop(r), 0);
  const perPortionKop = Math.round(totalKop / Math.max(1, portions));
  const marginKop = dishPrice - perPortionKop;
  const foodCostPct = dishPrice > 0 ? Math.round((perPortionKop / dishPrice) * 100) : null;

  const errors = useMemo(() => {
    const e: string[] = [];
    if (draft.some((r) => !r.itemId)) e.push('Выберите ингредиент во всех строках');
    if (draft.some((r) => !(r.quantity > 0))) e.push('Расход должен быть больше 0');
    const counts = new Map<string, number>();
    for (const r of draft) {
      if (!r.itemId) continue;
      counts.set(r.itemId, (counts.get(r.itemId) ?? 0) + 1);
    }
    if ([...counts.values()].some((n) => n > 1)) e.push('Один ингредиент указан несколько раз');
    return e;
  }, [draft]);

  const saveMut = useMutation({
    mutationFn: async () => {
      // Наружу всегда уходит расход на ОДНУ порцию — от него зависит списание.
      const payload = draft.map((r) => ({
        stockItemId: r.itemId,
        quantity: r.quantity / Math.max(1, portions),
      }));
      return saveDishTechCard(dishId, payload, portions > 1 ? portions : null);
    },
    onSuccess: () => {
      toast.success('Тех-карта сохранена');
      onChanged();
      onSaved?.();
      onClose();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Не удалось сохранить'),
  });

  const handleSave = () => {
    if (errors.length) { toast.error(errors[0]); return; }
    setSaving(true);
    saveMut.mutate(undefined, { onSettled: () => setSaving(false) });
  };

  // ⌘/Ctrl+Enter — сохранить, не тянясь к мыши.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault();
        handleSave();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, portions, errors]);

  const clearMut = useMutation({
    mutationFn: () => saveDishTechCard(dishId, [], null),
    onSuccess: () => {
      toast.success('Тех-карта удалена');
      onChanged();
      onSaved?.();
      onClose();
    },
  });

  if (isLoading || rows === null) return <Spinner />;

  return (
    <div className="grid gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-slate-500">
          Ингредиентом может быть и заготовка со своим составом — соус, тесто, маринад.
        </p>
        <button className="btn-outline text-xs" onClick={() => setShowCopy(true)}>
          <Copy size={13} /> Скопировать из блюда
        </button>
      </div>

      {/* Раскладка на N порций: удобно вводить «кастрюлю», а не порцию в уме. */}
      <div className="flex items-center gap-2 text-sm bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
        <span className="text-slate-500">Раскладка на</span>
        <NumberInput
          className="input w-20 text-right tabular-nums py-1"
          decimal={false}
          value={portions}
          onChange={(n) => setPortions(Math.max(1, Math.round(n) || 1))}
        />
        <span className="text-slate-500">{portionWord(portions)}</span>
        {portions > 1 && (
          <span className="text-xs text-slate-400 ml-auto">
            себестоимость порции = всё ÷ {portions}
          </span>
        )}
      </div>

      {manualCost != null && (
        <ManualCostWarning
          dishId={dishId}
          manualCost={manualCost}
          onCleared={() => { setManualCost(null); onChanged(); }}
        />
      )}

      {draft.length === 0 ? (
        <div className="text-center text-slate-400 py-6 text-sm border border-dashed border-slate-200 rounded-md">
          Пока нет ни одного ингредиента
        </div>
      ) : (
        <div className="grid gap-2">
          <div className="hidden sm:grid grid-cols-[1fr_170px_100px_36px] gap-2 text-[11px] uppercase tracking-wide text-slate-400 px-1">
            <div>Ингредиент</div>
            <div className="text-right">Расход{portions > 1 ? ` на ${portions}` : ''}</div>
            <div className="text-right">Стоимость</div>
            <div />
          </div>
          {draft.map((row) => (
            <IngredientRow
              key={row.key}
              row={row}
              items={items}
              usedIds={usedIds}
              autoFocus={focusRow.current === row.key}
              onSelect={(item) => patchRow(row.key, { itemId: item.id })}
              onCreate={(name) => setCreatingFor({ key: row.key, name })}
              onQuantity={(q) => patchRow(row.key, { quantity: q })}
              onRemove={() => removeRow(row.key)}
              onEnter={addRow}
              onOpenPrep={onOpenPrep}
              onMakePrep={onChanged}
              lineKop={lineKop(row)}
            />
          ))}
        </div>
      )}

      <button type="button" className="btn-outline self-start" onClick={addRow}>
        <Plus size={14} /> Добавить ингредиент
      </button>

      <CostSummary
        totalKop={totalKop}
        perUnitKop={perPortionKop}
        perUnitLabel="Себестоимость порции"
        showTotal={portions > 1}
        totalLabel={`Себестоимость всей раскладки (${portions})`}
        priceKop={dishPrice}
        priceLabel="Цена блюда"
        marginKop={marginKop}
        foodCostPct={foodCostPct}
      />

      <Warnings rows={draft} items={items} foodCostPct={foodCostPct} portions={portions} />

      {errors.length > 0 && (
        <ul className="text-sm text-rose-600 list-disc pl-5">
          {errors.map((e) => <li key={e}>{e}</li>)}
        </ul>
      )}

      <div className="flex flex-wrap justify-end gap-2 border-t border-slate-100 pt-3">
        {data && data.items.length > 0 && (
          <button
            className="btn-outline text-rose-600 border-rose-200 hover:bg-rose-50 mr-auto"
            onClick={() => { if (confirm('Удалить всю тех-карту блюда?')) clearMut.mutate(); }}
            disabled={saving}
          >
            <Trash2 size={14} /> Удалить
          </button>
        )}
        <button className="btn-outline" onClick={onClose} disabled={saving}>Отмена</button>
        <button
          className="btn-primary"
          onClick={handleSave}
          disabled={saving || errors.length > 0}
          title="⌘/Ctrl + Enter"
        >
          {saving ? 'Сохраняем…' : 'Сохранить'}
        </button>
      </div>

      {creatingFor && (
        <CreateStockItemModal
          initialName={creatingFor.name}
          onClose={() => setCreatingFor(null)}
          onCreated={(item) => {
            patchRow(creatingFor.key, { itemId: item.id });
            setCreatingFor(null);
            onChanged();
          }}
        />
      )}

      {showCopy && (
        <CopyFromDishModal
          dishId={dishId}
          onClose={() => setShowCopy(false)}
          onCopied={() => {
            setShowCopy(false);
            setRows(null); // перечитаем скопированную раскладку с сервера
            onChanged();
          }}
        />
      )}
    </div>
  );
}

// ─── Рецепт заготовки ────────────────────────────────────────────────────

function PrepPane({
  prepId, items, onOpenPrep, onChanged, onDone,
}: {
  prepId: string;
  items: StockItem[];
  onOpenPrep: (id: string, name: string) => void;
  onChanged: () => void;
  onDone: () => void;
}) {
  const [rows, setRows] = useState<Row[] | null>(null);
  const [loss, setLoss] = useState(0);
  const [mode, setMode] = useState<YieldMode>('raw');
  // Нужный выход, пока сырья в составе ещё нет (режим «по выходу»).
  const [targetNet, setTargetNet] = useState(1);
  // Выход «до потерь» вручную: штучные заготовки и составы без сырья в кг/л.
  const [manualOutput, setManualOutput] = useState<number | null>(null);
  // Старый состав, где выход вводили отдельно от сырья: потери пересчитаны из него.
  const [legacyNet, setLegacyNet] = useState<number | null>(null);
  const [trackStock, setTrackStock] = useState(false);
  const [creatingFor, setCreatingFor] = useState<{ key: string; name: string } | null>(null);
  const focusRow = useRef<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin/stock/prep-recipe', prepId],
    queryFn: () => getPrepRecipe(prepId),
  });

  const itemById = (id: string) => items.find((i) => i.id === id);
  const unitOf = (r: Row) => itemById(r.itemId)?.unit;
  const rawOf = (list: Row[]) => sumRaw(list.map((r) => ({ quantity: r.quantity, unit: unitOf(r) })));

  useEffect(() => {
    if (!data || rows !== null) return;
    const loaded = data.items.map((r) => ({ key: r.id ?? newKey(), itemId: r.componentId, quantity: r.quantity }));
    const storedOutput = data.outputQuantity && data.outputQuantity > 0 ? data.outputQuantity : 1;
    const storedLoss = data.wastePercent ?? 0;
    const storedNet = storedOutput * (1 - storedLoss / 100);
    const unitF = bulkFactor(data.unit);
    const raw = unitF == null ? 0 : rawOf(loaded) / unitF;
    setRows(loaded);
    setTrackStock(data.trackStock);
    setLoss(storedLoss);
    setTargetNet(storedNet > 0 ? round6(storedNet) : 1);
    if (unitF == null || (loaded.length > 0 && raw <= 0)) {
      setManualOutput(storedOutput);
      return;
    }
    if (loaded.length === 0) {
      setMode('output');
      return;
    }
    if (Math.abs(storedOutput - raw) <= Math.max(1e-6, raw * 1e-4)) return;
    // Выход вводили отдельно от сырья: оставляем то же нетто, разница — потери.
    const implied = (1 - storedNet / raw) * 100;
    if (implied >= 0 && implied <= MAX_LOSS_PERCENT) {
      setLoss(Math.round(implied * 1e4) / 1e4);
      setLegacyNet(storedNet);
    } else {
      setManualOutput(storedOutput);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const draft = rows ?? [];
  const usedIds = new Set(draft.map((r) => r.itemId).filter(Boolean));
  const lineKop = (r: Row) => Math.round(r.quantity * (itemById(r.itemId)?.costPerUnit ?? 0));
  const totalKop = draft.reduce((s, r) => s + lineKop(r), 0);
  const lossOk = loss >= 0 && loss <= MAX_LOSS_PERCENT;
  const k = 1 - (lossOk ? loss : 0) / 100;
  const prepUnit = data?.unit ?? 'кг';
  const prepF = bulkFactor(prepUnit);
  // Сырьё → потери → нетто. Вручную — только когда считать не от чего.
  const byYield = manualOutput == null && prepF != null;
  const rawBase = rawOf(draft); // кг/л
  const rawPrep = prepF ? rawBase / prepF : 0; // в единицах заготовки — это и есть выход «до потерь»
  const rawUnit = rawUnitOf(draft.map(unitOf));
  const net = byYield ? rawPrep * k : (manualOutput ?? 0) * k;
  const shownNet = byYield && mode === 'output' && rawBase <= 0 ? targetNet : net;
  const neededRaw = mode === 'output' && rawBase <= 0 ? (shownNet / k) * (prepF ?? 1) : rawBase;
  const perUnitKop = Math.round(totalKop / Math.max(net, 0.0001));
  const hasPieceRows = draft.some((r) => r.itemId && bulkFactor(unitOf(r)) == null);

  const addRow = () => {
    const key = newKey();
    focusRow.current = key;
    setRows([...draft, { key, itemId: '', quantity: 0 }]);
  };
  const patchRow = (key: string, patch: Partial<Row>) =>
    setRows(draft.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  /** Сырья должно стать needBase (кг/л): состав масштабируется, пропорции те же. */
  const scaleRawTo = (needBase: number) => {
    if (!(needBase > 0) || !Number.isFinite(needBase)) return;
    if (rawBase > 0) {
      const f = needBase / rawBase;
      setRows(draft.map((r) => ({ ...r, quantity: round6(r.quantity * f) })));
      return;
    }
    // Сырья ещё нет: единственному ингредиенту в кг/л ставим всё количество.
    const bulkRows = draft.filter((r) => bulkFactor(unitOf(r)) != null);
    if (bulkRows.length !== 1) return;
    const only = bulkRows[0];
    const f = bulkFactor(unitOf(only)) ?? 1;
    setRows(draft.map((r) => (r.key === only.key ? { ...r, quantity: round6(needBase / f) } : r)));
  };

  const changeNet = (n: number) => {
    setTargetNet(n);
    if (n > 0 && lossOk && prepF) scaleRawTo((n / k) * prepF);
  };

  const changeLoss = (l: number) => {
    setLoss(l);
    setLegacyNet(null);
    // «По выходу» нужный выход не меняется — пересчитывается сырьё.
    if (byYield && mode === 'output' && prepF && l >= 0 && l <= MAX_LOSS_PERCENT && shownNet > 0) {
      scaleRawTo((shownNet / (1 - l / 100)) * prepF);
    }
  };

  const selectItem = (row: Row, item: StockItem) => {
    const f = bulkFactor(item.unit);
    // «По выходу» первый ингредиент сразу получает нужное количество сырья.
    if (byYield && mode === 'output' && rawBase <= 0 && f != null && !(row.quantity > 0) && prepF && lossOk) {
      patchRow(row.key, { itemId: item.id, quantity: round6(((targetNet / k) * prepF) / f) });
      return;
    }
    patchRow(row.key, { itemId: item.id });
  };

  const saveMut = useMutation({
    mutationFn: () => savePrepRecipe(prepId, {
      items: draft.map((r) => ({ componentId: r.itemId, quantity: r.quantity })),
      // Выход «до потерь»: по сырью — сумма сырья, вручную — как ввели.
      outputQuantity: byYield
        ? (rawPrep > 0 ? round6(rawPrep) : data?.outputQuantity || 1)
        : (manualOutput ?? 1),
      trackStock,
      wastePercent: loss,
    }),
    onSuccess: () => {
      toast.success('Состав заготовки сохранён');
      onChanged();
      onDone();
    },
    // Замкнутый рецепт бэкенд отдаёт 409 с объяснением — показываем как есть.
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Не удалось сохранить'),
  });

  const errors: string[] = [];
  if (draft.some((r) => !r.itemId)) errors.push('Выберите ингредиент во всех строках');
  if (draft.some((r) => !(r.quantity > 0))) errors.push('Расход должен быть больше 0');
  if (!lossOk) errors.push(`Потери — от 0 до ${MAX_LOSS_PERCENT}%`);
  if (byYield && draft.some((r) => r.itemId) && !(rawPrep > 0)) {
    errors.push('Выход считается от сырья в кг, г, л или мл — добавьте такой ингредиент или укажите выход вручную');
  }
  if (!byYield && !((manualOutput ?? 0) > 0)) errors.push('Выход должен быть больше 0');

  if (isLoading || rows === null || !data) return <Spinner />;

  return (
    <div className="grid gap-3">
      {byYield ? (
        <>
          <YieldModeSwitch mode={mode} onChange={setMode} />
          <YieldModeNote mode={mode} />
          {legacyNet != null && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              Раньше выход был указан отдельно: {fmtYield(legacyNet)} {prepUnit} из {fmtYield(rawBase)} {rawUnit} сырья.
              Это потери {fmtPercent(loss)}% — себестоимость та&nbsp;же. Проверьте и сохраните.
            </div>
          )}
          <YieldInputs
            mode={mode}
            prepUnit={prepUnit}
            rawUnit={rawUnit}
            net={shownNet}
            raw={rawBase}
            loss={loss}
            lossOk={lossOk}
            onNet={changeNet}
            onRaw={scaleRawTo}
            onLoss={changeLoss}
          />
          <YieldCoefficient mode={mode} loss={lossOk ? loss : 0} raw={rawBase} rawUnit={rawUnit} />
          {(rawBase > 0 || mode === 'output') && (
            <YieldResult
              mode={mode}
              net={shownNet}
              raw={neededRaw}
              prepUnit={prepUnit}
              rawUnit={rawUnit}
              loss={lossOk ? loss : 0}
            />
          )}
        </>
      ) : (
        <div className="grid gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-slate-600">Выход до потерь</span>
            <NumberInput
              className="input w-24 py-1 text-right tabular-nums"
              value={manualOutput ?? 0}
              onChange={(n) => setManualOutput(n)}
            />
            <span className="text-slate-600">{prepUnit}</span>
            <span className="mx-1 text-slate-300">·</span>
            <span className="text-slate-600">потери</span>
            <NumberInput
              className="input w-20 py-1 text-right tabular-nums"
              value={loss}
              onChange={(n) => setLoss(n)}
            />
            <span className="text-slate-600">%</span>
          </div>
          <div className="text-xs text-slate-600">
            Готовый выход (нетто): <b className="tabular-nums">{fmtYield(net)} {prepUnit}</b>.{' '}
            {prepF == null
              ? 'Заготовка учитывается не в кг и не в л, поэтому выход указывается вручную.'
              : 'Выход указан вручную.'}
            {prepF != null && rawBase > 0 && (
              <>
                {' '}
                <button
                  type="button"
                  className="text-indigo-600 hover:underline"
                  onClick={() => { setManualOutput(null); setMode('raw'); }}
                >
                  Считать от сырья
                </button>
              </>
            )}
          </div>
        </div>
      )}

      <div className="text-sm font-medium text-slate-800">
        {!byYield
          ? `Ингредиенты (на ${fmtYield(manualOutput ?? 0)} ${prepUnit} до потерь)`
          : mode === 'output'
            ? `Ингредиенты (на ${fmtYield(shownNet)} ${prepUnit} готовой заготовки)`
            : `Ингредиенты (на выход ${fmtYield(net)} ${prepUnit})`}
      </div>

      {draft.length === 0 ? (
        <div className="text-center text-slate-400 py-6 text-sm border border-dashed border-slate-200 rounded-md">
          Добавьте ингредиенты заготовки
        </div>
      ) : (
        <div className="grid gap-2">
          <div className="hidden sm:grid grid-cols-[1fr_170px_100px_36px] gap-2 text-[11px] uppercase tracking-wide text-slate-400 px-1">
            <div>Ингредиент</div>
            <div className="text-right">Расход (брутто)</div>
            <div className="text-right">Стоимость</div>
            <div />
          </div>
          {draft.map((row) => (
            <IngredientRow
              key={row.key}
              row={row}
              items={items.filter((i) => i.id !== prepId)}
              usedIds={usedIds}
              autoFocus={focusRow.current === row.key}
              onSelect={(item) => selectItem(row, item)}
              onCreate={(name) => setCreatingFor({ key: row.key, name })}
              onQuantity={(q) => patchRow(row.key, { quantity: q })}
              onRemove={() => setRows(draft.filter((r) => r.key !== row.key))}
              onEnter={addRow}
              onOpenPrep={onOpenPrep}
              onMakePrep={onChanged}
              lineKop={lineKop(row)}
            />
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <button type="button" className="btn-outline" onClick={addRow}>
          <Plus size={14} /> Добавить ингредиент
        </button>
        {byYield && hasPieceRows && (
          <span className="text-xs text-slate-500">
            Штучные ингредиенты в сумму сырья не входят.{' '}
            <button
              type="button"
              className="text-indigo-600 hover:underline"
              onClick={() => setManualOutput(round6(rawPrep > 0 ? rawPrep : 1))}
            >
              Указать выход вручную
            </button>
          </span>
        )}
      </div>

      {byYield ? (
        <PrepCostPanel
          mode={mode}
          totalKop={totalKop}
          perUnitKop={perUnitKop}
          net={shownNet}
          prepUnit={prepUnit}
          loss={lossOk ? loss : 0}
        />
      ) : (
        <CostSummary
          totalKop={totalKop}
          perUnitKop={perUnitKop}
          perUnitLabel={loss > 0 && lossOk ? `Себестоимость 1 ${prepUnit} с учётом потерь` : `Себестоимость 1 ${prepUnit}`}
          showTotal
          totalLabel="Стоимость сырья (брутто)"
        />
      )}

      <label className="flex items-start gap-2 text-sm">
        <input
          type="checkbox"
          className="mt-1"
          checked={trackStock}
          onChange={(e) => setTrackStock(e.target.checked)}
        />
        <span>
          Вести остаток заготовки на складе
          <span className="block text-xs text-slate-400">
            Выключено — при продаже блюда списывается сырьё из состава. Включите, только если
            будете оформлять производство заготовки отдельно.
          </span>
        </span>
      </label>

      {errors.length > 0 && (
        <ul className="text-sm text-rose-600 list-disc pl-5">
          {errors.map((e) => <li key={e}>{e}</li>)}
        </ul>
      )}

      <div className="flex justify-end gap-2 border-t border-slate-100 pt-3">
        <button className="btn-outline" onClick={onDone}>Отмена</button>
        <button
          className="btn-primary"
          onClick={() => saveMut.mutate()}
          disabled={saveMut.isPending || errors.length > 0}
        >
          {saveMut.isPending ? 'Сохраняем…' : 'Сохранить состав'}
        </button>
      </div>

      {creatingFor && (
        <CreateStockItemModal
          initialName={creatingFor.name}
          onClose={() => setCreatingFor(null)}
          onCreated={(item) => {
            patchRow(creatingFor.key, { itemId: item.id });
            setCreatingFor(null);
            onChanged();
          }}
        />
      )}
    </div>
  );
}

// ─── Строка ингредиента ──────────────────────────────────────────────────

/** Названия, по которым обычно узнают заготовку — для мягкой подсказки. */
const PREP_HINT_WORDS = ['соус', 'тесто', 'бульон', 'крем', 'маринад', 'фарш', 'сироп', 'заготов', 'основа'];

function IngredientRow({
  row, items, usedIds, autoFocus, lineKop,
  onSelect, onCreate, onQuantity, onRemove, onEnter, onOpenPrep, onMakePrep,
}: {
  row: Row;
  items: StockItem[];
  usedIds: Set<string>;
  autoFocus?: boolean;
  lineKop: number;
  onSelect: (item: StockItem) => void;
  onCreate: (name: string) => void;
  onQuantity: (q: number) => void;
  onRemove: () => void;
  onEnter: () => void;
  onOpenPrep: (id: string, name: string) => void;
  onMakePrep: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const item = items.find((i) => i.id === row.itemId);
  const isPrep = item?.kind === 'PREP';
  const looksLikePrep = !!item && !isPrep
    && PREP_HINT_WORDS.some((w) => item.name.toLowerCase().includes(w));

  const makePrepMut = useMutation({
    mutationFn: () => setStockItemKind(row.itemId, 'PREP'),
    onSuccess: () => {
      onMakePrep();
      if (item) onOpenPrep(item.id, item.name);
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Не удалось'),
  });

  return (
    <div className="rounded-md border border-slate-100 p-2 sm:border-0 sm:p-0">
      <div className="grid sm:grid-cols-[1fr_170px_100px_36px] gap-2 items-start">
        <div>
          <div className="flex items-center gap-1">
            {isPrep && (
              <button
                type="button"
                className="p-1 text-slate-400 hover:text-slate-700 shrink-0"
                onClick={() => setExpanded((v) => !v)}
                title="Показать состав заготовки"
              >
                {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </button>
            )}
            <div className="flex-1 min-w-0">
              <IngredientPicker
                items={items}
                value={row.itemId}
                excludeIds={usedIds}
                autoFocus={autoFocus}
                onSelect={onSelect}
                onCreate={onCreate}
              />
            </div>
          </div>
          {item && (
            <div className="text-xs text-slate-400 mt-1 pl-1">
              {quantityHint(row.quantity, item.unit, item.costPerUnit, formatRub)}
              {!isPrep && ` · на складе ${formatQuantity(item.quantity, item.unit)}`}
            </div>
          )}
          {isPrep && (
            <button
              type="button"
              className="text-xs text-violet-700 hover:underline mt-1 pl-1"
              onClick={() => onOpenPrep(item!.id, item!.name)}
            >
              Изменить состав заготовки
            </button>
          )}
          {looksLikePrep && (
            <div className="text-xs text-slate-500 mt-1 pl-1">
              Похоже на заготовку.{' '}
              <button
                type="button"
                className="text-indigo-600 hover:underline"
                onClick={() => makePrepMut.mutate()}
                disabled={makePrepMut.isPending}
              >
                Собрать её состав
              </button>
              {' '}— тогда себестоимость посчитается сама.
            </div>
          )}
        </div>

        <QuantityField
          value={row.quantity}
          unit={item?.unit ?? 'шт'}
          onChange={onQuantity}
          onEnter={onEnter}
        />

        <div className="text-right text-sm tabular-nums text-slate-600 pt-2">
          {item ? formatRub(lineKop) : '—'}
        </div>

        <button
          type="button"
          className="h-10 w-9 flex items-center justify-center rounded-md text-rose-500 hover:bg-rose-50"
          onClick={onRemove}
          title="Убрать"
        >
          <Trash2 size={16} />
        </button>
      </div>

      {isPrep && expanded && <PrepPreview prepId={item!.id} />}
    </div>
  );
}

/** Состав заготовки прямо в строке — чтобы не уходить со страницы блюда. */
function PrepPreview({ prepId }: { prepId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['admin/stock/prep-recipe', prepId],
    queryFn: () => getPrepRecipe(prepId),
  });

  if (isLoading) return <div className="text-xs text-slate-400 pl-8 py-2">Загружаем состав…</div>;
  if (!data || data.items.length === 0) {
    return (
      <div className="text-xs text-slate-400 pl-8 py-2">
        Состав пока не заполнен — нажмите «Изменить состав заготовки».
      </div>
    );
  }

  return (
    <div className="mt-1 ml-8 mb-1 rounded-md bg-violet-50/60 border border-violet-100 p-2 text-xs">
      <div className="text-violet-800 mb-1">
        Выход {fmtYield(data.effectiveOutput ?? data.outputQuantity ?? 1)} {data.unit}
        {data.wastePercent ? ` · потери ${fmtPercent(data.wastePercent)}%` : ''}: {formatRub(data.costPerUnit)}/{data.unit}
      </div>
      <div className="grid gap-0.5">
        {data.items.map((r) => (
          <div key={r.componentId} className="flex justify-between gap-2 text-slate-600">
            <span className="truncate">{r.component.name}</span>
            <span className="tabular-nums shrink-0">
              {formatQuantity(r.quantity, r.component.unit)}
              <span className="text-slate-400 ml-2">
                {formatRub(Math.round(r.quantity * r.component.costPerUnit))}
              </span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Итоги и предупреждения ──────────────────────────────────────────────

function CostSummary({
  totalKop, perUnitKop, perUnitLabel, showTotal, totalLabel,
  priceKop, priceLabel, marginKop, foodCostPct,
}: {
  totalKop: number;
  perUnitKop: number;
  perUnitLabel: string;
  showTotal?: boolean;
  totalLabel?: string;
  priceKop?: number;
  priceLabel?: string;
  marginKop?: number;
  foodCostPct?: number | null;
}) {
  return (
    <div className="border-t border-slate-200 pt-3 grid gap-1.5 text-sm">
      {showTotal && totalLabel && (
        <div className="flex items-center justify-between text-slate-500">
          <span>{totalLabel}</span>
          <span className="tabular-nums">{formatRub(totalKop)}</span>
        </div>
      )}
      <div className="flex items-center justify-between">
        <span className="text-slate-500">{perUnitLabel}</span>
        <span className="font-semibold tabular-nums">{formatRub(perUnitKop)}</span>
      </div>
      {priceKop != null && priceKop > 0 && (
        <>
          <div className="flex items-center justify-between">
            <span className="text-slate-500">{priceLabel}</span>
            <span className="tabular-nums">{formatRub(priceKop)}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-slate-500">Маржа с порции</span>
            <span className={`font-semibold tabular-nums ${(marginKop ?? 0) < 0 ? 'text-rose-600' : 'text-emerald-700'}`}>
              {formatRub(marginKop ?? 0)}
              {foodCostPct != null && (
                <span className="ml-2 text-xs font-normal text-slate-400">фудкост {foodCostPct}%</span>
              )}
            </span>
          </div>
        </>
      )}
      {priceKop === 0 && (
        <div className="text-xs text-slate-400">
          У блюда не задана цена — укажите её в меню, чтобы видеть маржу.
        </div>
      )}
    </div>
  );
}

/**
 * Сторожа от абсурдных цифр. Прежний редактор молча соглашался с расходом «1 кг
 * мяса на порцию» — владелец видел фудкост 3574% и не понимал, что не так.
 */
function Warnings({
  rows, items, foodCostPct, portions,
}: {
  rows: Row[];
  items: StockItem[];
  foodCostPct: number | null;
  portions: number;
}) {
  const list: Array<{ text: string; tone: 'danger' | 'warn' }> = [];

  const zeroPriced = rows
    .map((r) => items.find((i) => i.id === r.itemId))
    .filter((i): i is StockItem => !!i && i.costPerUnit <= 0);
  if (zeroPriced.length > 0) {
    list.push({
      tone: 'warn',
      text: `Не задана цена: ${zeroPriced.map((i) => i.name).join(', ')}. Себестоимость посчитается неполной — оформите приход или впишите цену в позиции склада.`,
    });
  }

  // Расход от 1 кг/л на ОДНУ порцию почти всегда означает путаницу единиц.
  const bigPerPortion = rows.filter((r) => {
    const item = items.find((i) => i.id === r.itemId);
    if (!item) return false;
    const u = item.unit.toLowerCase();
    return (u === 'кг' || u === 'л') && r.quantity / Math.max(1, portions) >= 1;
  });
  if (bigPerPortion.length > 0) {
    const names = bigPerPortion
      .map((r) => items.find((i) => i.id === r.itemId)?.name)
      .filter(Boolean)
      .join(', ');
    list.push({
      tone: 'warn',
      text: `На порцию уходит 1 ${'кг/л'} и больше: ${names}. Если имелись в виду граммы — переключите единицу рядом с полем.`,
    });
  }

  if (foodCostPct != null && foodCostPct > 100) {
    list.push({
      tone: 'danger',
      text: `Себестоимость выше цены блюда (фудкост ${foodCostPct}%). Проверьте единицы измерения и расход.`,
    });
  } else if (foodCostPct != null && foodCostPct >= 60) {
    list.push({ tone: 'warn', text: `Высокий фудкост — ${foodCostPct}%. Обычно держат до 35-40%.` });
  }

  if (list.length === 0) return null;

  return (
    <div className="grid gap-2">
      {list.map((w) => (
        <div
          key={w.text}
          className={`flex items-start gap-2 text-sm rounded-md px-3 py-2 border ${
            w.tone === 'danger'
              ? 'bg-rose-50 border-rose-200 text-rose-700'
              : 'bg-amber-50 border-amber-200 text-amber-800'
          }`}
        >
          <AlertTriangle size={15} className="shrink-0 mt-0.5" />
          <span>{w.text}</span>
        </div>
      ))}
    </div>
  );
}

/** Ручная закупочная цена перебивает тех-карту в аналитике — молчать нельзя. */
function ManualCostWarning({
  dishId, manualCost, onCleared,
}: {
  dishId: string;
  manualCost: number;
  onCleared: () => void;
}) {
  const mut = useMutation({
    mutationFn: async () => {
      const { updateDish } = await import('@/services/admin');
      return updateDish(dishId, { costPrice: null });
    },
    onSuccess: () => {
      toast.success('Теперь себестоимость считается по тех-карте');
      onCleared();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Не удалось'),
  });

  return (
    <div className="flex flex-wrap items-center gap-2 text-sm bg-amber-50 border border-amber-200 text-amber-900 rounded-md px-3 py-2">
      <AlertTriangle size={15} className="shrink-0" />
      <span className="flex-1 min-w-0">
        У блюда задана ручная закупочная цена {formatRub(manualCost)} — в аналитике используется
        она, а не эта тех-карта.
      </span>
      <button
        className="btn-outline text-xs shrink-0"
        onClick={() => mut.mutate()}
        disabled={mut.isPending}
      >
        Считать по тех-карте
      </button>
    </div>
  );
}

// ─── Вспомогательные модалки ─────────────────────────────────────────────

/** Создание позиции склада, не выходя из тех-карты. */
function CreateStockItemModal({
  initialName, onClose, onCreated,
}: {
  initialName: string;
  onClose: () => void;
  onCreated: (item: StockItem) => void;
}) {
  const [name, setName] = useState(initialName);
  const [unit, setUnit] = useState('кг');
  const [priceRub, setPriceRub] = useState(0);
  const [asPrep, setAsPrep] = useState(false);

  const mut = useMutation({
    mutationFn: async () => {
      const item = await createStock({
        name: name.trim(),
        unit,
        costPerUnit: Math.round(priceRub * 100),
      });
      if (asPrep) await setStockItemKind(item.id, 'PREP');
      return { ...item, kind: asPrep ? ('PREP' as const) : ('GOODS' as const) };
    },
    onSuccess: (item) => {
      toast.success('Позиция создана');
      onCreated(item);
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Не удалось создать'),
  });

  return (
    <Modal
      open
      onClose={onClose}
      title="Новая позиция склада"
      size="sm"
      footer={
        <>
          <button className="btn-outline" onClick={onClose}>Отмена</button>
          <button
            className="btn-primary"
            onClick={() => mut.mutate()}
            disabled={mut.isPending || name.trim().length === 0}
          >
            Создать
          </button>
        </>
      }
    >
      <div className="grid gap-3">
        <div>
          <label className="label">Название</label>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Единица учёта</label>
            <select className="input" value={unit} onChange={(e) => setUnit(e.target.value)}>
              {CANONICAL_UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Цена за единицу, ₽</label>
            <NumberInput className="input text-right tabular-nums" value={priceRub} onChange={setPriceRub} />
          </div>
        </div>
        <label className="flex items-start gap-2 text-sm">
          <input type="checkbox" className="mt-1" checked={asPrep} onChange={(e) => setAsPrep(e.target.checked)} />
          <span>
            Это заготовка своего производства
            <span className="block text-xs text-slate-400">
              Цену считать не нужно — соберём её из состава.
            </span>
          </span>
        </label>
        <p className="text-xs text-slate-400">
          Остаток и фасовку можно указать позже во вкладке «Позиции».
        </p>
      </div>
    </Modal>
  );
}

/** Копирование раскладки другого блюда — вариации отличаются двумя строчками. */
function CopyFromDishModal({
  dishId, onClose, onCopied,
}: {
  dishId: string;
  onClose: () => void;
  onCopied: () => void;
}) {
  const [q, setQ] = useState('');
  const { data: dishes = [] } = useQuery({
    queryKey: ['admin/menu/dishes'],
    queryFn: () => listDishes(),
  });
  const { data: cards = [] } = useQuery({
    queryKey: ['admin/stock/tech-cards'],
    queryFn: async () => (await import('@/services/admin')).listTechCards(),
  });

  // Копировать можно только у блюд, где тех-карта есть.
  const withCards = useMemo(() => {
    const ids = new Set(cards.map((c) => c.dishId));
    const list = dishes.filter((d) => d.id !== dishId && ids.has(d.id));
    const query = q.trim().toLowerCase();
    return query ? list.filter((d) => d.name.toLowerCase().includes(query)) : list;
  }, [dishes, cards, dishId, q]);

  const mut = useMutation({
    mutationFn: (sourceId: string) => copyDishTechCard(dishId, sourceId),
    onSuccess: () => {
      toast.success('Раскладка скопирована');
      onCopied();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Не удалось скопировать'),
  });

  return (
    <Modal
      open
      onClose={onClose}
      title="Скопировать тех-карту"
      size="sm"
      footer={<button className="btn-outline" onClick={onClose}>Закрыть</button>}
    >
      <div className="grid gap-3">
        <input
          className="input"
          placeholder="Поиск блюда…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          autoFocus
        />
        {withCards.length === 0 ? (
          <div className="text-sm text-slate-400 text-center py-4">
            Нет блюд с готовой тех-картой
          </div>
        ) : (
          <div className="max-h-72 overflow-auto divide-y divide-slate-100 border border-slate-200 rounded-md">
            {withCards.map((d) => (
              <button
                key={d.id}
                type="button"
                className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm"
                onClick={() => mut.mutate(d.id)}
                disabled={mut.isPending}
              >
                {d.name}
              </button>
            ))}
          </div>
        )}
        <p className="text-xs text-slate-400">
          Текущий состав блюда будет заменён скопированным.
        </p>
      </div>
    </Modal>
  );
}

function portionWord(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 'порцию';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return 'порции';
  return 'порций';
}
